import * as Sentry from '@sentry/react';
import { useAtomValue } from 'jotai';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlaidLink } from 'react-plaid-link';
import { loadStripe, Stripe } from '@stripe/stripe-js';

import dayjs from 'dayjs';
import Card, { CardNames } from '../../../components/Card/Card.tsx';
import PlaidIcon from '../../../assets/cards/PlaidIcon.svg?react';
import Config from '../../../lib/Config.ts';
import { getPhone } from '../../../lib/getPhone.ts';
import { IS_EMBED } from '../../../lib/isEmbed.ts';
// Keep createPlaidPaymentMethod first: on one line, a 24-char name right after
// the word "Plaid" trips gitleaks' plaid-client-id rule (a false positive on the
// function name). These are API wrappers, not credentials.
import { createPlaidPaymentMethod, createPlaidLinkToken } from '../../../lib/api.ts';
import {
  AuthAtom,
  PaymentMethodsAtom,
  usePaymentMethods,
  useRestaurant,
  useRestaurantId,
  useUserDetails,
  useWidgetState,
  widgetPaymentMethodDeselection,
  widgetPaymentMethodSelection,
} from '../lib/state.ts';
import { track, resolveBookingType } from '@/lib/analytics/index.ts';
import {
  allowsAchPaymentType,
  allowsCardWalletPaymentType,
  allowsWirePaymentType,
  buildPaymentMethodOptions,
  isBankPaymentMethod,
  getPaymentMethodLast4,
  getPaymentMethodIconName,
} from '../lib/paymentTypes.ts';
import SegmentedControl from '../../../components/SegmentedControl/SegmentedControl.tsx';
import type { useBookingPreview, BookingPreview } from '../lib/hooks/useBookingPreview.ts';
import { IDV_REQUIRED_COPY } from '@/lib/apiErrorCode.ts';
import {
  MINIMUM_SPEND_DEPOSIT_LABEL,
  isRestaurantTableMinimum,
  restaurantAmountDueApplies,
  restaurantTableMinimumCurrency,
  restaurantTableMinimumFromWidget,
  showsMinimumSpendExplanation,
} from '@/lib/labels.ts';
import type { PaymentMethod } from '@/lib/types.ts';
import {
  RestaurantAmountDueRow,
  RestaurantMinimumSpendTotals,
} from './RestaurantMinimumSpendTotals.tsx';
import { formatTimeRange } from '../CustomRequest/utils.ts';
import { CardEntry } from './CardEntry.tsx';
import { EventCheckoutGate } from './EventCheckoutGate.tsx';

import styles from '../EventPage/EventPage.module.css';

// Stripe constants + card form + billing validation now live in ./CardForm.tsx
// (shared with the Dorsia-origin payment iframe).

// ── Main component ──────────────────────────────────────────────────────────

/**
 * Unified payment page for both restaurant and event bookings.
 * Desktop keeps the order summary in a right-hand sticky column.
 * On a narrow viewport and inside the embed, the summary sits between
 * Details and Payment. Detects allowed payment types (ACH, card, wire)
 * and renders the appropriate payment sections.
 */
const PaymentPage = () => (
  <EventCheckoutGate>
    {(bookingPreview) => <DorsiaPaymentPage bookingPreview={bookingPreview} />}
  </EventCheckoutGate>
);

const DorsiaPaymentPage = ({
  bookingPreview,
}: {
  bookingPreview: ReturnType<typeof useBookingPreview>;
}) => {
  usePaymentMethods();
  const { restaurant } = useRestaurant();
  const [widget, updateWidget] = useWidgetState();
  const user = useUserDetails();
  const auth = useAtomValue(AuthAtom);
  const navigate = useNavigate();
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const redirectedRef = useRef(false);

  const methods = useAtomValue(PaymentMethodsAtom);

  // ── Booking preview (handles both restaurant + event APIs) ──────────────
  const {
    preview,
    loading: previewLoading,
    error: previewError,
    errorCode: previewErrorCode,
  } = bookingPreview;

  // ── Payment type detection ──────────────────────────────────────────────
  // Merge payment types from restaurant config + event ticket config.
  // The UI is agnostic to bookingType — it shows whatever's configured.
  // The backend processes the payment_method_id based on its own rules.
  const allowedTypes = useMemo(() => {
    const restaurantTypes = restaurant?.allowed_payment_types ?? [];
    const eventTypes =
      widget.bookingType === 'event' ? (widget.eventInfo?.allowedPaymentTypes ?? []) : [];
    // Prefer restaurant-level config; fall back to event-level
    if (restaurantTypes.length > 0) return restaurantTypes;
    if (eventTypes.length > 0) return eventTypes;
    return [];
  }, [
    restaurant?.allowed_payment_types,
    widget.eventInfo?.allowedPaymentTypes,
    widget.bookingType,
  ]);

  const hasConfig = allowedTypes.length > 0;
  const supportsCard = !hasConfig || allowsCardWalletPaymentType(allowedTypes);
  // Events default to ACH ON unless the config explicitly excludes it (matches old EventPayment behavior).
  // Restaurant bookings only show ACH when explicitly configured.
  const isEvent = widget.bookingType === 'event';
  const supportsAch = isEvent
    ? !hasConfig || allowsAchPaymentType(allowedTypes)
    : allowsAchPaymentType(allowedTypes);
  const supportsWire = allowsWirePaymentType(allowedTypes);
  const requiresAch = supportsAch && !supportsCard;
  const isWireOnly = supportsWire && !supportsCard && !supportsAch;

  // ── Payment method toggle ───────────────────────────────────────────────
  const paymentMethodOptions = useMemo(
    () => buildPaymentMethodOptions(allowedTypes),
    [allowedTypes]
  );

  const [selectedPaymentType, setSelectedPaymentType] = useState<string>(
    () => paymentMethodOptions[0]?.value ?? 'card_wallet'
  );

  // Sync selection when options change (async restaurant data load)
  useEffect(() => {
    if (
      paymentMethodOptions.length > 0 &&
      !paymentMethodOptions.find((o) => o.value === selectedPaymentType)
    ) {
      setSelectedPaymentType(paymentMethodOptions[0].value);
    }
  }, [paymentMethodOptions, selectedPaymentType]);

  // ── Derive saved methods ────────────────────────────────────────────────
  const savedBankMethods = useMemo(
    () => (methods ?? []).filter((m) => isBankPaymentMethod(m)),
    [methods]
  );

  const savedCards = useMemo(
    () => (methods ?? []).filter((m) => !isBankPaymentMethod(m)),
    [methods]
  );

  const savedBank = savedBankMethods[0] ?? null;

  const selectedMethod = useMemo(
    () => (methods ?? []).find((m) => m.id === widget.payment_method),
    [methods, widget.payment_method]
  );

  // ── ACH (Plaid) state — seed from widget persistence OR saved bank ─────
  const [achLinked, setAchLinked] = useState(!!widget.ach_payment_id || !!savedBank);
  const [achBankName, setAchBankName] = useState(widget.ach_bank_name ?? '');
  const [achLast4, setAchLast4] = useState(widget.ach_bank_last4 ?? savedBank?.number ?? '');

  // ── Card state — NOT auto-selected; user must explicitly pick ──────────
  const [cardAdded, setCardAdded] = useState(!!widget.payment_method);
  const [cardBrand, setCardBrand] = useState('');
  const [cardLast4, setCardLast4] = useState('');

  // ── Plaid Link token ───────────────────────────────────────────────────
  const [plaidLinkToken, setPlaidLinkToken] = useState<string | null>(null);

  // ── Load Stripe ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!supportsCard && !supportsAch) {
      // No need for Stripe if wire-only
      return;
    }
    const load = async () => {
      setStripe(await loadStripe(Config.data.stripe.key));
    };
    void load();
  }, [supportsCard, supportsAch]);

  // ── Fetch Plaid link token when ACH is supported ───────────────────────
  useEffect(() => {
    if (!supportsAch || !auth?.token) {
      setPlaidLinkToken(null);
      return;
    }
    const fetchLinkToken = async () => {
      try {
        const response = await createPlaidLinkToken(auth.token);
        setPlaidLinkToken(response.create_link_token ?? response.link_token ?? null);
      } catch (e) {
        Sentry.captureException(e);
      }
    };
    void fetchLinkToken();
  }, [supportsAch, auth?.token]);

  // ── Auto-detect saved bank when methods load asynchronously ────────────
  useEffect(() => {
    if (!methods) return;
    if (savedBank && !achLinked) {
      setAchLinked(true);
      setAchLast4(savedBank.number);
    }
  }, [methods, savedBank, achLinked]);

  // ── Auto-select default bank method (from classic Payment.tsx) ─────────
  useEffect(() => {
    if (!requiresAch || widget.payment_method || savedBankMethods.length === 0) {
      return;
    }

    const defaultBank = savedBankMethods.find((m) => m.default) ?? savedBankMethods[0];

    // Deliberately NOT widgetPaymentMethodSelection(): auto-selection must not
    // wipe a failure banner the guest was bounced back here to read [HOS-2029]
    updateWidget({
      payment_method: defaultBank.id,
      ach_payment_id: defaultBank.id,
      ach_bank_name: defaultBank.nickname ?? widget.ach_bank_name,
      ach_bank_last4: getPaymentMethodLast4(defaultBank),
    });
  }, [requiresAch, savedBankMethods, updateWidget, widget.payment_method, widget.ach_bank_name]);

  // ── Enforce payment method type constraints ────────────────────────────
  useEffect(() => {
    if (!widget.payment_method || !selectedMethod) return;

    const isBank = isBankPaymentMethod(selectedMethod);

    // ACH-only: reject card selection
    if (requiresAch && !isBank) {
      updateWidget({ payment_method: undefined });
      return;
    }

    // Card-only: reject bank selection
    if (!requiresAch && !supportsAch && isBank) {
      updateWidget({ payment_method: undefined });
    }
  }, [requiresAch, supportsAch, selectedMethod, updateWidget, widget.payment_method]);

  // ── Sync selected card display when methods load ───────────────────────
  useEffect(() => {
    if (!methods || !widget.payment_method) return;

    const method = methods.find((m) => m.id === widget.payment_method);
    if (!method) return;

    const brand = method.brand?.toLowerCase() ?? '';
    const isBank = !brand || brand === 'us_bank_account' || brand === 'ach';
    if (isBank) return;

    setCardAdded(true);
    setCardBrand(method.brand ?? 'Card');
    setCardLast4(method.number ?? '****');
  }, [methods, widget.payment_method]);

  // ── Zero-dollar free event detection ──────────────────────────────────
  // Use the ticket's base price (not total_paid) so that paid tickets
  // discounted to $0 by credits/Fun Coupons still require a payment method.
  const isZeroDollarNoPayment =
    widget.bookingType === 'event' &&
    (widget.option?.price ?? -1) === 0 &&
    !!restaurant?.allow_zero_dollar_booking_links_without_payment_method;

  // ── Guard: redirect if no booking type set ─────────────────────────────
  useEffect(() => {
    if (redirectedRef.current) return;

    if (!widget.bookingType) {
      redirectedRef.current = true;
      updateWidget({
        payment_error: 'Please select your booking before continuing.',
      });
      navigate('../booking', { replace: true });
      return;
    }

    // No auth → send to details to log in
    if (!auth?.token) {
      redirectedRef.current = true;
      navigate('../details', { replace: true });
      return;
    }

    // $0 free event with flag → skip payment page entirely
    if (isZeroDollarNoPayment) {
      redirectedRef.current = true;
      navigate('../checkout', { replace: true });
    }
  }, [widget.bookingType, auth?.token, isZeroDollarNoPayment, updateWidget, navigate]);

  // ── Loading states (Stripe/restaurant still loading) ──────────────────
  // Wire-only doesn't need the parent Stripe object. Neither does the EMBED card
  // path: card entry is a Dorsia-origin `/embed/pay` iframe (EmbeddedPaymentHost)
  // with its own Stripe, so the parent `loadStripe()` is irrelevant there — and if
  // the partner's CSP blocks js.stripe.com, `stripe` stays null forever. Gating the
  // whole page on it would leave the embed payment step blank; skip the check under
  // IS_EMBED so the iframe still renders.
  if (!restaurant || (!stripe && !isWireOnly && !IS_EMBED) || !auth || !user) {
    return <></>;
  }

  const user_phone = getPhone(user.phone, widget.phone_country);

  // For dual-payment flows: need both bank + backup card. Otherwise: just a card.
  const hasCardSelection = !!widget.payment_method;
  const canContinue =
    selectedPaymentType === 'ach' ? achLinked && hasCardSelection : hasCardSelection;

  return (
    <div className={`${styles.twoColumnLayout} ${styles.summaryAsideLayout}`}>
      <div className={styles.summaryAsideStack}>
        <div className={styles.summaryAsideLead}>
          {/* Always-mounted live region so the alert announces even when the guest
            lands here with the error already set [HOS-2029]. API card-failure
            messages are complete sentences (e.g. "Your card has expired."). */}
          <div role="alert" className={widget?.payment_error ? styles.error : undefined}>
            {widget?.payment_error}
          </div>

          {previewError && (
            <div className={styles.error}>
              {previewErrorCode === 'IDV_REQUIRED'
                ? IDV_REQUIRED_COPY
                : 'This item is currently unavailable for booking. Please go back and select a different option.'}
            </div>
          )}

          {widget.eventInfo?.requires_idv && (
            <div className={styles.infoBanner}>
              Identity verification is required for this booking. You&apos;ll receive instructions
              by text after confirming your reservation.
            </div>
          )}

          {/* ── Details (read-only) ── */}
          <div className={styles.card}>
            <div className={styles.contactSummary}>
              <div className={styles.contactSummaryHeader}>
                <h2 className={styles.contactSummaryTitle}>Details</h2>
                <button
                  type="button"
                  className={styles.editLink}
                  onClick={() => navigate('../details')}
                >
                  Edit
                </button>
              </div>

              <div className={styles.contactColumns}>
                <div className={styles.contactColumn}>
                  <p className={styles.contactLabel}>MEMBER DETAILS</p>
                  <p className={styles.contactValue}>
                    {user.first_name} {user.last_name}
                  </p>
                  <p className={styles.contactValue}>
                    {!!user_phone && user_phone.formatNational()}
                  </p>
                  <p className={styles.contactValue}>{user.email}</p>
                </div>

                {restaurant?.allow_alternative_pickup && widget.alternative_pickup_first_name && (
                  <div className={styles.contactColumn}>
                    <p className={styles.contactLabel}>ALTERNATE PICKUP</p>
                    <p className={styles.contactValue}>
                      {widget.alternative_pickup_first_name} {widget.alternative_pickup_last_name}
                    </p>
                    <p className={styles.contactValue}>{widget.alternative_pickup_phone}</p>
                    <p className={styles.contactValue}>{widget.alternative_pickup_email}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.summaryAsideTrail}>
          {/* ── Payment Section ── */}
          <div className={`${styles.card} ${styles.mt20}`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <h2 className={styles.contactSummaryTitle}>Payment</h2>

              <SegmentedControl
                options={paymentMethodOptions}
                selectedValue={selectedPaymentType}
                onValueChange={setSelectedPaymentType}
                testId="payment-method-toggle"
              />

              {/* Card payment */}
              {selectedPaymentType === 'card_wallet' && (
                <>
                  <p className={styles.aboutText} style={{ color: '#8d8d8d' }}>
                    Select a saved payment method or add a new card.
                  </p>

                  {/* Saved cards */}
                  {savedCards.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <p style={savedMethodsLabelStyle}>Saved payment methods</p>
                      {savedCards.map((method) => {
                        const isSelected = widget.payment_method === method.id;
                        const brandKey = (method.brand?.toLowerCase() ?? '') as CardNames;
                        return (
                          <button
                            key={method.id}
                            type="button"
                            onClick={() => updateWidget(widgetPaymentMethodSelection(method.id))}
                            style={{
                              ...savedMethodBaseStyle,
                              border: isSelected ? '2px solid #0d0d0d' : '1px solid #E5E5E5',
                              background: isSelected ? '#FAFAFA' : '#FFFFFF',
                            }}
                          >
                            <span style={savedMethodIconStyle}>
                              <Card name={brandKey} className="" />
                            </span>
                            <span
                              style={{
                                flex: 1,
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                              }}
                            >
                              <span
                                style={{ color: '#8d8d8d', fontSize: 14, letterSpacing: '2px' }}
                              >
                                &bull;&bull;&bull;&bull;
                              </span>
                              <span style={{ fontWeight: 500 }}>{method.number}</span>
                            </span>
                            {isSelected ? (
                              <span style={savedMethodSelectedBadge}>Selected</span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Add new card */}
                  <div className={styles.paymentCard}>
                    <p className={styles.paymentCardName} style={{ marginBottom: 12 }}>
                      {savedCards.length > 0 ? 'Or add a new card' : 'Add a credit card'}
                    </p>
                    <CardEntry
                      stripe={stripe}
                      onSuccess={(brand: string, last4: string) => {
                        setCardAdded(true);
                        setCardBrand(brand);
                        setCardLast4(last4);
                      }}
                    />
                  </div>
                </>
              )}

              {/* ACH payment */}
              {selectedPaymentType === 'ach' && (
                <>
                  {/* Plaid ACH section */}
                  <div className={styles.paymentCard}>
                    <div className={styles.paymentCardHeader}>
                      <div className={styles.paymentCardTitle}>
                        <PlaidIcon style={{ width: 24, height: 24, flexShrink: 0 }} />
                        <div>
                          <p className={styles.paymentCardName}>Plaid ACH</p>
                          <p className={styles.paymentCardSubtitle}>
                            Link a bank account to pay for this booking
                          </p>
                        </div>
                      </div>
                      {achLinked && <span className={styles.badgeLinked}>Linked</span>}
                    </div>

                    {/* Saved bank accounts */}
                    {savedBankMethods.length > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                          margin: '12px 0',
                        }}
                      >
                        <p style={savedMethodsLabelStyle}>Saved bank accounts</p>
                        {savedBankMethods.map((method) => {
                          const isSelected = widget.payment_method === method.id;
                          return (
                            <button
                              key={method.id}
                              type="button"
                              onClick={() => {
                                setAchLinked(true);
                                setAchLast4(getPaymentMethodLast4(method));
                                setAchBankName(method.nickname ?? '');
                                updateWidget(
                                  widgetPaymentMethodSelection(method.id, {
                                    ach_payment_id: method.id,
                                    ach_bank_name: method.nickname ?? widget.ach_bank_name,
                                    ach_bank_last4: getPaymentMethodLast4(method),
                                  })
                                );
                              }}
                              style={{
                                ...savedMethodBaseStyle,
                                border: isSelected ? '2px solid #0d0d0d' : '1px solid #E5E5E5',
                                background: isSelected ? '#FAFAFA' : '#FFFFFF',
                              }}
                            >
                              <span style={savedMethodIconStyle}>
                                <Card name={getPaymentMethodIconName(method)} className="" />
                              </span>
                              <span
                                style={{
                                  flex: 1,
                                  textAlign: 'left',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6,
                                }}
                              >
                                <span style={{ fontWeight: 500 }}>
                                  {method.nickname || 'Bank account'}
                                </span>
                                <span
                                  style={{ color: '#8d8d8d', fontSize: 14, letterSpacing: '2px' }}
                                >
                                  &bull;&bull;&bull;&bull; {getPaymentMethodLast4(method)}
                                </span>
                              </span>
                              {isSelected && <span style={savedMethodSelectedBadge}>Selected</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {achLinked && savedBankMethods.length === 0 && (
                      <p className={styles.aboutText}>
                        {achBankName ? `${achBankName} ` : ''}&bull;&bull;&bull;&bull; {achLast4} —
                        Verified for payment
                      </p>
                    )}

                    {!achLinked && (
                      <PlaidLinkButton
                        linkToken={plaidLinkToken}
                        authToken={auth.token}
                        onSuccess={(bankName, last4, paymentId) => {
                          setAchLinked(true);
                          setAchBankName(bankName);
                          setAchLast4(last4);
                          updateWidget(
                            widgetPaymentMethodSelection(paymentId, {
                              ach_payment_id: paymentId,
                              ach_bank_name: bankName,
                              ach_bank_last4: last4,
                            })
                          );
                        }}
                      />
                    )}
                  </div>

                  {/* "and" divider + incidentals card */}
                  <div className={styles.andDivider}>
                    <div className={styles.andDividerLine} />
                    <span className={styles.andDividerText}>and</span>
                    <div className={styles.andDividerLine} />
                  </div>

                  <div className={styles.paymentCard}>
                    <div className={styles.paymentCardHeader}>
                      <div className={styles.paymentCardTitle}>
                        <svg className={styles.paymentCardIcon} viewBox="0 0 32 32" fill="none">
                          <rect
                            x="2"
                            y="4"
                            width="28"
                            height="16"
                            rx="2"
                            stroke="#0D0A06"
                            strokeWidth="1.5"
                          />
                          <path d="M2 9h28" stroke="#0D0A06" strokeWidth="1.5" />
                          <path
                            d="M7 15h6"
                            stroke="#0D0A06"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                        <div>
                          <p className={styles.paymentCardName}>Credit Card</p>
                          <p className={styles.paymentCardSubtitle}>Used for incidentals only</p>
                        </div>
                      </div>
                      {cardAdded && <span className={styles.badgeSelected}>Selected</span>}
                    </div>

                    {cardAdded ? (
                      <>
                        <p className={styles.aboutText}>
                          {cardBrand.toUpperCase()} &bull;&bull;&bull;&bull; {cardLast4} — Selected
                          for incidentals
                        </p>
                        <button
                          type="button"
                          className={styles.editLink}
                          onClick={() => {
                            setCardAdded(false);
                            setCardBrand('');
                            setCardLast4('');
                            updateWidget(widgetPaymentMethodDeselection());
                          }}
                          style={{ marginTop: 4 }}
                        >
                          Change Card
                        </button>
                      </>
                    ) : (
                      <>
                        {/* Pick from saved cards */}
                        {savedCards.length > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 8,
                              margin: '12px 0',
                            }}
                          >
                            {savedCards.map((method) => {
                              const brandKey = (method.brand?.toLowerCase() ?? '') as CardNames;
                              return (
                                <button
                                  key={method.id}
                                  type="button"
                                  onClick={() => {
                                    setCardAdded(true);
                                    setCardBrand(method.brand);
                                    setCardLast4(method.number);
                                    updateWidget(widgetPaymentMethodSelection(method.id));
                                  }}
                                  style={{
                                    ...savedMethodBaseStyle,
                                    border: '1px solid #E5E5E5',
                                    background: '#FFFFFF',
                                  }}
                                >
                                  <span style={savedMethodIconStyle}>
                                    <Card name={brandKey} className="" />
                                  </span>
                                  <span
                                    style={{
                                      flex: 1,
                                      textAlign: 'left',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: '#8d8d8d',
                                        fontSize: 14,
                                        letterSpacing: '2px',
                                      }}
                                    >
                                      &bull;&bull;&bull;&bull;
                                    </span>
                                    <span style={{ fontWeight: 500 }}>{method.number}</span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* Add new card form */}
                        <CardEntry
                          stripe={stripe}
                          onSuccess={(brand: string, last4: string) => {
                            setCardAdded(true);
                            setCardBrand(brand);
                            setCardLast4(last4);
                          }}
                        />
                      </>
                    )}
                  </div>
                </>
              )}

              {/* Wire transfer */}
              {selectedPaymentType === 'wire' && (
                <>
                  <div className={styles.paymentCard}>
                    <p className={styles.paymentCardName}>Wire Transfer</p>
                    <p className={styles.aboutText} style={{ color: '#8d8d8d' }}>
                      Wire transfer instructions will be sent after booking is confirmed.
                    </p>
                  </div>

                  {/* Credit card for incidentals */}
                  <div className={styles.paymentCard}>
                    <p className={styles.paymentCardName}>Credit Card</p>
                    <p className={styles.aboutText} style={{ color: '#8d8d8d' }}>
                      A credit card is required for incidentals.
                    </p>

                    {cardAdded ? (
                      <>
                        <div className={styles.cardConfirmation}>
                          <Card name={(cardBrand?.toLowerCase() ?? '') as CardNames} className="" />
                          <span>•••• {cardLast4}</span>
                        </div>
                        <button
                          type="button"
                          className={styles.changeCardButton}
                          onClick={() => {
                            setCardAdded(false);
                            setCardBrand('');
                            setCardLast4('');
                            updateWidget(widgetPaymentMethodDeselection());
                          }}
                        >
                          Change Card
                        </button>
                      </>
                    ) : (
                      <>
                        {/* Pick from saved cards */}
                        {savedCards.length > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 8,
                              margin: '12px 0',
                            }}
                          >
                            {savedCards.map((method) => {
                              const brandKey = (method.brand?.toLowerCase() ?? '') as CardNames;
                              return (
                                <button
                                  key={method.id}
                                  type="button"
                                  onClick={() => {
                                    setCardAdded(true);
                                    setCardBrand(method.brand);
                                    setCardLast4(method.number);
                                    updateWidget(widgetPaymentMethodSelection(method.id));
                                  }}
                                  style={{
                                    ...savedMethodBaseStyle,
                                    border: '1px solid #E5E5E5',
                                    background: '#FFFFFF',
                                  }}
                                >
                                  <span style={savedMethodIconStyle}>
                                    <Card name={brandKey} className="" />
                                  </span>
                                  <span
                                    style={{
                                      flex: 1,
                                      textAlign: 'left',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        color: '#8d8d8d',
                                        fontSize: 14,
                                        letterSpacing: '2px',
                                      }}
                                    >
                                      &bull;&bull;&bull;&bull;
                                    </span>
                                    <span style={{ fontWeight: 500 }}>{method.number}</span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}

                        {/* Add new card form */}
                        <CardEntry
                          stripe={stripe}
                          onSuccess={(brand: string, last4: string) => {
                            setCardAdded(true);
                            setCardBrand(brand);
                            setCardLast4(last4);
                          }}
                        />
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div
            style={{
              position: 'sticky',
              bottom: 0,
              background: 'linear-gradient(to top, #fff 80%, transparent)',
              padding: '24px 0 8px',
              zIndex: 10,
            }}
          >
            <button
              className={styles.primaryButton}
              disabled={!canContinue}
              onClick={() =>
                navigate(
                  widget.bookingType === 'custom_request' ? '../request/checkout' : '../checkout'
                )
              }
            >
              Continue
            </button>
          </div>
        </div>
      </div>

      <div className={styles.sidebarColumn}>
        <OrderSummary
          preview={preview}
          loading={previewLoading}
          restaurant={restaurant}
          widget={widget}
          paymentMethod={selectedMethod}
        />
      </div>
    </div>
  );
};

// ── Sub-components ──────────────────────────────────────────────────────────

/**
 * ACH embed-overlay compatibility (verified 2026-07-10, HOS-1817).
 *
 * Unlike card entry (iframe-isolated via `/embed/pay`), ACH runs INSIDE the
 * shadow-root overlay. Plaid Link renders its own modal as a full-page overlay
 * iframe in the HOST page's LIGHT DOM (`document.body`) — a sibling of the
 * `<dorsia-reservation-widget>` element, never inside our (closed) shadow root —
 * so it works from the embed without iframe-wrapping. Verified compatible:
 *  - Focus trap: `GlassOverlay`'s Tab-wrap is shadow-scoped (queries `.dorsia-stage`
 *    and checks `shadowRoot.activeElement`); Plaid's cross-origin iframe is not a
 *    descendant and its keystrokes don't bubble to our document, so the trap cannot
 *    fight Plaid's modal focus — no suspend/restore needed.
 *  - Z-index: our overlay is `2147483000` (`embed/overlay.css`, `embed/skeleton.ts`),
 *    deliberately below Plaid's `2147483647`, so Plaid's modal renders above us.
 *  - Cancel: `onExit` resets `loading` and returns to the ACH step with state intact.
 *  - Security: no bank credentials or Plaid secrets ever leave Plaid's own iframe or
 *    transit our postMessage/analytics/Sentry payloads. On success we send only Plaid's
 *    short-lived `public_token` and the selected `account_id` to our OWN backend via
 *    `createPlaidPaymentMethod`; the exchange for a bank-account token happens server-side.
 *
 * Partner-page CSP requirements for ACH (documented for the EMBED.md guide, HOS-1834;
 * we document, we do not enforce a partner's CSP). Per Plaid's official Link Web docs
 * (https://plaid.com/docs/link/web/), a partner whose site sets a CSP must allow:
 *   default-src     https://cdn.plaid.com/
 *   script-src      'unsafe-inline' https://cdn.plaid.com/link/v2/stable/link-initialize.js
 *   style-src       'unsafe-inline'
 *   style-src-elem  'unsafe-inline'
 *   style-src-attr  'unsafe-inline'
 *   frame-src       https://cdn.plaid.com/
 *   connect-src     https://production.plaid.com/   (sandbox: https://sandbox.plaid.com/)
 * (A nonce may replace 'unsafe-inline' for script-src/style-src/style-src-elem — see the
 * docs' nonce variant. No img-src directive is required.)
 *
 * The live Plaid sandbox click-through (link -> checkout, cancel, mobile) is deferred
 * human verification — see the PR body and HUMAN-CHECKLIST.md.
 */
function PlaidLinkButton({
  linkToken,
  authToken,
  onSuccess,
}: {
  linkToken: string | null;
  authToken: string;
  onSuccess: (bankName: string, last4: string, paymentId: string) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const restaurantId = useRestaurantId();
  const [widget] = useWidgetState();

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (publicToken, metadata) => {
      setLoading(true);
      setError(undefined);
      try {
        const accountId = metadata.accounts[0]?.id;
        if (!accountId) {
          setError('No bank account selected.');
          setLoading(false);
          return;
        }

        const response = await createPlaidPaymentMethod(authToken, {
          public_token: publicToken,
          account_id: accountId,
        });

        const paymentId = response.payment_method?.id;
        if (!paymentId) {
          setError('Unable to save bank account. Please try again.');
          setLoading(false);
          return;
        }

        const bankName =
          response.payment_method?.bank_name ??
          metadata.institution?.name ??
          metadata.accounts[0]?.name ??
          'Bank';
        const last4 = response.payment_method?.last4 ?? metadata.accounts[0]?.mask ?? '****';

        if (restaurantId != null) {
          track('Widget Payment Method Added', {
            restaurant_id: restaurantId,
            booking_type: resolveBookingType(widget.bookingType),
            payment_type: 'ach',
          });
        }

        onSuccess(bankName, last4, paymentId);
      } catch (e) {
        Sentry.captureException(e);
        setError('Failed to link bank account. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    onExit: () => setLoading(false),
  });

  return (
    <>
      {error && <div className={styles.error}>{error}</div>}
      <button
        className={styles.primaryButton}
        disabled={loading || !ready || !linkToken}
        onClick={() => open()}
      >
        {loading ? 'Linking...' : 'Link Bank Account'}
      </button>
    </>
  );
}

// ── Order Summary ───────────────────────────────────────────────────────────

function OrderSummary({
  preview,
  loading,
  restaurant,
  widget,
  paymentMethod = null,
}: {
  preview: BookingPreview | null;
  loading: boolean;
  restaurant: {
    name?: string;
    address?: string;
    neighborhood_name?: string;
    wide_crop_image_url?: string;
    currency_symbol?: string;
    features?: {
      HideMinimumSpendExplanation?: boolean;
    };
  };
  widget: ReturnType<typeof useWidgetState>[0];
  paymentMethod?: Pick<PaymentMethod, 'brand' | 'number'> | null;
}) {
  const restaurantName = preview?.restaurant?.name ?? restaurant.name ?? '';
  const restaurantAddress = preview?.restaurant?.address ?? restaurant.address;
  const neighborhoodName = preview?.restaurant?.neighborhood_name ?? restaurant.neighborhood_name;
  const isCustomRequest = widget.bookingType === 'custom_request';
  const showMinimumSpendExplanation = showsMinimumSpendExplanation(restaurant.features);
  // Interactive-map event venues frame the ticket price as a minimum-spend deposit.
  // Applied only to the first row below (ticket/access, requested before add-ons) so
  // add-on lines aren't mislabeled; gated on event — line items also render for classic.
  const depositSuffix =
    widget.venue_map_interactive && widget.bookingType === 'event'
      ? ` ${MINIMUM_SPEND_DEPOSIT_LABEL}`
      : '';
  const cr = widget.custom_request;

  return (
    <div className={styles.orderSummary}>
      <div className="flex flex-col gap-1">
        <p className={styles.orderSummaryTitle}>{restaurantName}</p>
        {restaurantAddress && <p className={styles.orderSummaryAddress}>{restaurantAddress}</p>}
        {neighborhoodName && <p className={styles.orderSummaryAddress}>{neighborhoodName}</p>}
      </div>

      <div className={styles.orderSummaryDivider} />

      {/* Booking info */}
      <div className={styles.orderSummaryBooking}>
        {restaurant.wide_crop_image_url && (
          <img
            src={restaurant.wide_crop_image_url}
            alt={widget.event?.name ?? restaurantName}
            className={styles.orderSummaryImage}
          />
        )}
        <div className={styles.orderSummaryBookingDetails}>
          <p className={styles.orderSummaryBookingName}>
            {isCustomRequest ? 'Custom Request Details' : (widget.event?.name ?? restaurantName)}
          </p>

          {/* Date/time: prefer preview data, fall back to widget */}
          {isCustomRequest && cr ? (
            <>
              {cr.date && (
                <p className={styles.orderSummaryBookingInfo}>
                  {dayjs(cr.date).format('ddd, MMMM D')}
                </p>
              )}
              {cr.selected_times?.length > 0 && (
                <p className={styles.orderSummaryBookingInfo}>
                  {formatTimeRange(cr.selected_times)}
                </p>
              )}
            </>
          ) : preview ? (
            <>
              {preview.date_formatted && (
                <p className={styles.orderSummaryBookingInfo}>{preview.date_formatted}</p>
              )}
              {preview.time_formatted && (
                <p className={styles.orderSummaryBookingInfo}>{preview.time_formatted}</p>
              )}
            </>
          ) : widget.bookingType === 'event' ? (
            <>
              {widget.event?.datetime_local__formatted && (
                <p className={styles.orderSummaryBookingInfo}>
                  {widget.event.datetime_local__formatted.date_dow_long},{' '}
                  {widget.event.datetime_local__formatted.date_long}
                </p>
              )}
              {widget.event?.datetime_local__formatted?.time && (
                <p className={styles.orderSummaryBookingInfo}>
                  {widget.event.datetime_local__formatted.time}
                </p>
              )}
            </>
          ) : (
            <>
              {widget.confirm_body?.date && (
                <p className={styles.orderSummaryBookingInfo}>
                  {dayjs(widget.confirm_body.date).format('ddd, MMMM D')}
                </p>
              )}
              {widget.confirm_body?.time && (
                <p className={styles.orderSummaryBookingInfo}>{widget.confirm_body.time}</p>
              )}
            </>
          )}

          {/* Party size */}
          {isCustomRequest && cr ? (
            <p className={styles.orderSummaryBookingInfo}>
              {cr.party_size} {cr.party_size === 1 ? 'guest' : 'guests'}
            </p>
          ) : (preview?.party_size ?? widget.eventInfo?.party_size ?? widget.option?.seats) ? (
            <p className={styles.orderSummaryBookingInfo}>
              {(() => {
                const size =
                  preview?.party_size ?? widget.eventInfo?.party_size ?? widget.option?.seats ?? 0;
                return `${size} ${size === 1 ? 'guest' : 'guests'}`;
              })()}
            </p>
          ) : null}

          {/* Table type */}
          {isCustomRequest && cr?.selected_table_type_names?.length ? (
            <p className={styles.orderSummaryBookingInfo}>
              {cr.selected_table_type_names.join(', ')}
            </p>
          ) : (preview?.table_type ?? widget.option?.type) ? (
            <p className={styles.orderSummaryBookingInfo}>
              {preview?.table_type ?? widget.option?.type}
            </p>
          ) : null}

          {/* Request expiration shown inline with booking details */}
          {isCustomRequest && cr?.expiration_description && (
            <p className={styles.orderSummaryBookingInfo}>Expires: {cr.expiration_description}</p>
          )}
        </div>
      </div>

      {/* Custom request pricing summary */}
      {isCustomRequest && cr && (
        <>
          <div className={styles.orderSummaryDivider} />

          <div className="flex flex-col gap-2">
            <div className={styles.orderSummaryPriceRow}>
              <p className={styles.orderSummaryPriceLabel}>Min. Spend / Person</p>
              <p className={styles.orderSummaryPriceValue}>
                {restaurant.currency_symbol ?? '$'}
                {cr.per_person_spend}
              </p>
            </div>

            <div className={styles.orderSummaryPriceRow}>
              <p className={styles.orderSummaryPriceLabel}>Party Size</p>
              <p className={styles.orderSummaryPriceValue}>
                {cr.party_size} {cr.party_size === 1 ? 'guest' : 'guests'}
              </p>
            </div>
          </div>

          <div className={styles.orderSummaryDivider} />

          <div className={styles.orderSummaryTotal}>
            <p className={styles.orderSummaryTotalLabel}>Total Minimum Spend</p>
            <p className={styles.orderSummaryTotalValue}>
              {restaurant.currency_symbol ?? '$'}
              {cr.per_person_spend * cr.party_size}
            </p>
          </div>
        </>
      )}

      {/* Standard booking pricing (non-custom-request) */}
      {!isCustomRequest &&
        (() => {
          const isEvent = widget.bookingType === 'event';
          const totalPaid = preview?.total_paid ?? 0;
          const widgetMinimum = restaurantTableMinimumFromWidget({
            isEvent,
            pricePerPerson: widget.option?.price,
            partySize: preview?.party_size ?? widget.confirm_body?.party,
            formattedPricePerPerson: widget.option?.price__currency,
          });
          const feeRestaurant = preview?.fee_restaurant || widgetMinimum?.amount || 0;
          const feeCurrency = preview?.fee_restaurant__currency ?? widgetMinimum?.currency;
          const pricePerPerson = preview?.price_per_person ?? widget.option?.price;
          const pricePerPersonCurrency =
            preview?.price_per_person__currency ?? widget.option?.price__currency;
          const isMinSpend = isRestaurantTableMinimum({
            isEvent,
            totalPaid,
            feeRestaurant,
            feeCurrency,
            pricePerPerson,
          });

          return (
            <>
              {/* Item-level breakdown — skip for minimum-spend restaurant bookings */}
              {!isMinSpend &&
                preview?.items &&
                preview.items.length > 0 &&
                preview.items.map((item, idx) => (
                  <div className={styles.orderSummaryBooking} key={idx}>
                    <div className={styles.orderSummaryBookingDetails}>
                      <p className={styles.orderSummaryBookingInfo}>
                        {item.name}: {item.quantity} x {item.price__currency}
                        {idx === 0 ? depositSuffix : ''}
                      </p>
                    </div>
                  </div>
                ))}

              {/* Fall back to widget eventInfo items — skip for minimum-spend */}
              {!isMinSpend &&
                !preview &&
                widget.eventInfo?.items &&
                typeof widget.eventInfo.items === 'object' &&
                Object.values(widget.eventInfo.items).map(
                  (
                    item: {
                      item?: { name?: string; image?: string };
                      price__currency?: string;
                      quantity?: number;
                    },
                    idx: number
                  ) => (
                    <div className={styles.orderSummaryBooking} key={idx}>
                      <div className={styles.orderSummaryBookingDetails}>
                        <p className={styles.orderSummaryBookingInfo}>
                          {item.item?.name}: {item.quantity} x {item.price__currency}
                          {idx === 0 ? depositSuffix : ''}
                        </p>
                      </div>
                    </div>
                  )
                )}

              <div className={styles.orderSummaryDivider} />

              {loading && (
                <p className={styles.orderSummaryBookingInfo} style={{ color: '#8d8d8d' }}>
                  Loading pricing...
                </p>
              )}

              {/* Subtotal — skip for minimum-spend restaurant bookings */}
              {!isMinSpend &&
                (preview?.fee_restaurant__currency ||
                  widget.eventInfo?.fee_restaurant__currency ||
                  widget.option?.price__currency) && (
                  <div className={styles.orderSummaryPriceRow}>
                    <p className={styles.orderSummaryPriceLabel}>Subtotal</p>
                    <p className={styles.orderSummaryPriceValue}>
                      {preview?.fee_restaurant__currency ??
                        widget.eventInfo?.fee_restaurant__currency ??
                        widget.option?.price__currency}
                    </p>
                  </div>
                )}

              {/* Tax */}
              {(() => {
                const taxAmount = preview?.tax_amount ?? widget.eventInfo?.tax_amount ?? 0;
                const taxRate = preview?.tax_rate ?? widget.eventInfo?.tax_rate;
                const taxCurrency =
                  preview?.tax_amount__currency ?? widget.eventInfo?.tax_amount__currency;
                if (!taxAmount || taxAmount <= 0) return null;
                return (
                  <div className={styles.orderSummaryPriceRow}>
                    <p className={styles.orderSummaryPriceLabel}>
                      Tax{taxRate ? ` (${taxRate}%)` : ''}
                    </p>
                    <p className={styles.orderSummaryPriceValue}>{taxCurrency}</p>
                  </div>
                );
              })()}

              {/* Service charges */}
              {preview?.service_charges?.map((charge, idx) => (
                <div className={styles.orderSummaryPriceRow} key={idx}>
                  <p className={styles.orderSummaryPriceLabel}>{charge.label}</p>
                  <p className={styles.orderSummaryPriceValue}>{charge.amount__currency}</p>
                </div>
              ))}

              {/* Fall back to widget service charges if preview not loaded */}
              {!preview &&
                widget.eventInfo?.service_charges__currency?.map(
                  (charge: { label: string; amount: string }, idx: number) => (
                    <div className={styles.orderSummaryPriceRow} key={idx}>
                      <p className={styles.orderSummaryPriceLabel}>{charge.label}</p>
                      <p className={styles.orderSummaryPriceValue}>{charge.amount}</p>
                    </div>
                  )
                )}

              {(() => {
                const walletApplied = !!(
                  preview?.credits_used__currency ||
                  widget.eventInfo?.credits_used__currency ||
                  preview?.points_used_currency_value__currency
                );
                const amountDueCurrency =
                  totalPaid > 0 ? (preview?.total_paid__currency ?? feeCurrency) : null;
                const cardName = paymentMethod ? getPaymentMethodIconName(paymentMethod) : null;

                return (
                  <>
                    {walletApplied && (
                      <div className={styles.orderSummaryWalletLines}>
                        {(preview?.credits_used__currency ||
                          widget.eventInfo?.credits_used__currency) && (
                          <div className={styles.orderSummaryPriceRow}>
                            <p className={styles.orderSummaryPriceLabel}>Credits Applied</p>
                            <p className={styles.orderSummaryPriceValue}>
                              -
                              {preview?.credits_used__currency ??
                                widget.eventInfo?.credits_used__currency}
                            </p>
                          </div>
                        )}
                        {preview?.points_used_currency_value__currency && (
                          <div className={styles.orderSummaryPriceRow}>
                            <p className={styles.orderSummaryPriceLabel}>Fun Coupons</p>
                            <p className={styles.orderSummaryPriceValue}>
                              -{preview.points_used_currency_value__currency}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Amount Due is the card charge. Show it when the wallet reduces
                        the charge, or when the charge differs from the bold Minimum
                        spend (e.g. prepaid tax or service charges). totalPaid is
                        authoritative here (no credits toggle on this step). */}
                    {isMinSpend &&
                      amountDueCurrency &&
                      restaurantAmountDueApplies({
                        walletApplied,
                        cardChargeAmount: totalPaid,
                        pricePerPerson,
                        partySize: preview?.party_size ?? widget.confirm_body?.party,
                        feeRestaurant,
                      }) && (
                        <RestaurantAmountDueRow
                          amountDueCurrency={amountDueCurrency}
                          cardName={cardName}
                          cardLast4={paymentMethod ? getPaymentMethodLast4(paymentMethod) : null}
                        />
                      )}
                  </>
                );
              })()}

              {!isMinSpend && <div className={styles.orderSummaryDivider} />}

              {/* Total / Minimum spend. One block so the summary's 20px row gap
                  does not open space between the per-person line, the total, and the caption. */}
              {isMinSpend ? (
                <RestaurantMinimumSpendTotals
                  pricePerPersonCurrency={pricePerPersonCurrency}
                  minimumSpendCurrency={restaurantTableMinimumCurrency({
                    pricePerPerson,
                    partySize: preview?.party_size ?? widget.confirm_body?.party,
                    feeRestaurant,
                    feeCurrency,
                  })}
                  showCaption={showMinimumSpendExplanation}
                  paymentMethodLast4={paymentMethod ? getPaymentMethodLast4(paymentMethod) : null}
                  paymentMethodCardName={
                    paymentMethod ? getPaymentMethodIconName(paymentMethod) : null
                  }
                />
              ) : (
                (() => {
                  const totalCurrency =
                    preview?.total_paid__currency ??
                    widget.eventInfo?.total_paid__currency ??
                    widget.option?.price__currency;
                  if (!totalCurrency && !feeCurrency) return null;
                  return (
                    <div className={styles.orderSummaryTotal}>
                      <p className={styles.orderSummaryTotalLabel}>Total</p>
                      <p className={styles.orderSummaryTotalValue}>
                        {totalCurrency ?? feeCurrency}
                      </p>
                    </div>
                  );
                })()
              )}
            </>
          );
        })()}
    </div>
  );
}

// ── Inline styles (hoisted outside component, static) ───────────────────────

const savedMethodsLabelStyle: React.CSSProperties = {
  fontFamily: "'Sailec', sans-serif",
  fontSize: 13,
  fontWeight: 500,
  color: '#8d8d8d',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  margin: 0,
};

const savedMethodBaseStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '14px 16px',
  borderRadius: 10,
  cursor: 'pointer',
  fontFamily: "'Sailec', sans-serif",
  fontSize: 15,
  color: '#0d0d0d',
  width: '100%',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease, background 0.15s ease',
};

const savedMethodIconStyle: React.CSSProperties = {
  flexShrink: 0,
  width: 36,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const savedMethodSelectedBadge: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  background: '#0d0d0d',
  color: '#fff',
  padding: '3px 8px',
  borderRadius: 6,
  textTransform: 'uppercase',
  flexShrink: 0,
  letterSpacing: '0.3px',
};

export default PaymentPage;
